import IntlMessages from 'helpers/IntlMessages';
import { useContext, useRef, useState } from 'react';
import { UserContext } from 'contexts/user/UserContext';
import { errorReferenceCodes, getErrorDiagnostics, getAdminErrorMessages, getReportDiagnostics } from 'helpers/errorDiagnostics';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';
import AlertCircleOutlineIcon from 'mdi-react/AlertCircleOutlineIcon';
import { getShipCrewChatUrl, getReportPagePath } from 'helpers/shipCrewChat';
import ErrorReportForm from './ErrorReportForm';
import { Alert, Button, Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

export default function ErrorFallback({ error: receivedError, eventId = undefined, resetErrorBoundary = undefined, inline = false }) {
  const error = receivedError ?? {};
  const history = useHistory();
  const intl = useIntl();
  const userContext = useContext(UserContext);
  const diagnostics = getErrorDiagnostics(error);
  const reportDiagnostics = getReportDiagnostics(error);
  const reportPageUrl = `${window.location.origin}${getReportPagePath(window.location.pathname)}`;
  const referenceCode = errorReferenceCodes[diagnostics.kind];
  const candidateEventId = eventId ?? reportDiagnostics.eventId;
  const safeEventId = typeof candidateEventId === 'string' && /^[a-f0-9]{32}$/i.test(candidateEventId) ? candidateEventId : null;
  const shipCrewChatUrl = getShipCrewChatUrl(import.meta.env.VITE_DISCOURSE_ERROR_REPORTS_CHAT_URL);
  const canReport = Boolean(shipCrewChatUrl && !error.messageId);
  const [showErrorModal, setShowErrorModal] = useState(true);
  const detailsRef = useRef(null);
  const [copyState, setCopyState] = useState('idle');
  const [reportUnavailable, setReportUnavailable] = useState(false);
  const reportReference = `${referenceCode}${safeEventId ? ` / ${safeEventId}` : ''}`;
  const reportText = [reportReference, reportPageUrl,
    reportDiagnostics.httpStatus && intl.formatMessage({ id: 'error.diagnostic-status' }, { status: reportDiagnostics.httpStatus }),
    reportDiagnostics.operationName && intl.formatMessage({ id: 'error.report-operation' }, { operation: reportDiagnostics.operationName }),
    ...reportDiagnostics.details,
  ].filter(Boolean).join('\n');
  const emailBase = `mailto:info@greensteps.me?subject=${encodeURIComponent(intl.formatMessage({ id: 'error.email-subject' }, { reference: referenceCode }))}&body=`;
  const fullEmailUrl = emailBase + encodeURIComponent(reportText);
  const emailUrl = fullEmailUrl.length <= 2000 ? fullEmailUrl : emailBase + encodeURIComponent([
    reportReference, reportPageUrl, intl.formatMessage({ id: 'error.email-paste-details' }),
  ].join('\n'));
  const copyDetails = async () => {
    try {
      await navigator.clipboard.writeText(reportText);
      setCopyState('copied');
    } catch {
      detailsRef.current?.focus();
      detailsRef.current?.select();
      setCopyState('manual');
    }
  };
  const handleClose = () => {
    setShowErrorModal(false);
    resetErrorBoundary && resetErrorBoundary();
    error.reload && history.go(0);
  };
  const redirectId =
    error.redirect && !intl.formatMessage({ id: `redirect.${error.redirect}` }).includes('.') ? `redirect.${error.redirect}` : 'redirect.back';
  const handleRedirect = () => {
    const redirectUrl = `/${error.redirect}`;
    error.reload ? (window.location.href = redirectUrl) : history.push(redirectUrl);
  };

  const content = (
      <Alert style={{ margin: 0, backgroundColor: '#fff', color: 'inherit', border: 0 }} variant="light">
        <Alert.Heading className="text-danger">
          <AlertCircleOutlineIcon aria-hidden="true" className="mr-2" />
          <IntlMessages id="error.error-title" />
        </Alert.Heading>
        <div style={{ 'textAlign': 'justify', whiteSpace: 'pre-wrap' }}>
          {error.messageId ? <IntlMessages id={`error.${error.messageId}`} /> : <IntlMessages id="error.unexpected-friendly" />}
        </div>
        {!error.messageId && (!canReport || reportUnavailable) && (
          <div className="mt-3 mb-0">
            <p className="mb-2"><IntlMessages id="error.reference-code" /></p>
            <div className="d-flex align-items-start">
              <textarea ref={detailsRef} className="form-control flex-grow-1" readOnly rows={Math.min(8, Math.max(3, reportText.split('\n').length))}
                style={{ fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 1.4 }}
                aria-label={intl.formatMessage({ id: 'error.report-details' })} value={reportText} />
              <Button type="button" variant="outline-secondary" className="ml-2" onClick={copyDetails}
                aria-label={intl.formatMessage({ id: 'error.copy-details' })}
                title={intl.formatMessage({ id: 'error.copy-details' })}>
                <ContentCopyIcon aria-hidden="true" />
              </Button>
            </div>
            {copyState !== 'idle' && <p role="status" className="mt-2 mb-0"><IntlMessages id={`error.copy-${copyState}`} /></p>}
            <p className="mt-2 mb-0"><Button variant="success" href={emailUrl}><IntlMessages id="error.contact-email" /></Button></p>
          </div>
        )}
        {canReport && <ErrorReportForm onUnavailable={() => setReportUnavailable(true)} code={referenceCode} eventId={safeEventId} diagnostics={getReportDiagnostics(error)} />}
        {userContext?.currentUser?.isAdmin === true && (
          <details className="mt-3">
            <summary><IntlMessages id="error.admin-diagnostics" /></summary>
            <p className="mt-2"><IntlMessages id={`error.diagnostic-${diagnostics.kind}`} /></p>
            {diagnostics.httpStatus && <p>{intl.formatMessage({ id: 'error.diagnostic-status' }, { status: diagnostics.httpStatus })}</p>}
            <p><IntlMessages id="error.diagnostic-details" /></p>
            {getAdminErrorMessages(error).map((message, index) => (
              <pre key={index} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{message}</pre>
            ))}
          </details>
        )}
        <div className="d-flex justify-content-end">
          {error.redirect && (
            <Button className="mr-2" onClick={handleRedirect}>
              <IntlMessages id={redirectId} />
            </Button>
          )}
          &nbsp;
          {!inline && <Button className="mr-2" variant="outline-danger" onClick={handleClose}>
            <IntlMessages id="general.close" />
          </Button>}
        </div>
      </Alert>
  );

  return inline ? content : <Modal show={showErrorModal} onHide={handleClose}>{content}</Modal>;
}
